"use client"

import { useState } from "react"
import { useAuth } from "@/context/AuthContext"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { LogOut, Loader2, Check } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useNavigate } from "react-router-dom"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { supabase } from "@/lib/supabase"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"

const ProfilePage = () => {
  const { t, i18n } = useTranslation(["common", "profile"]);
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [resetSent, setResetSent] = useState(false)
  const [resetLoading, setResetLoading] = useState(false)

  const isRTL = i18n.language === "ar"

  if (!user) {
    return <div>Loading...</div>
  }

  const getInitials = (email: string) => {
    return email?.substring(0, 2).toUpperCase() || "U"
  }

  const handleResetPassword = async () => {
    if (!user?.email) return

    setResetLoading(true)
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/update-password`,
      })

      if (error) {
        toast.error(t("profile:toast.error.title"), {
          description: error.message || t("profile:toast.error.description"),
          icon: true,
        });
      } else {
        setResetSent(true)
        toast.success(t("profile:toast.success.title"), {
          description: t("profile:toast.success.description"),
        })
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error(t("profile:toast.error.title"), {
        description: t("profile:toast.error.description"),
      });
    } finally {
      setResetLoading(false)
    }
  }

  return (
    <div
      className={cn(
        "container mx-auto px-4 py-8",
        isRTL ? "rtl" : "ltr"
      )}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <h1
        className={cn(
          "text-3xl font-bold mb-6 text-center",
          isRTL ? "text-right" : "text-left"
        )}
      >
        {t("profile:page.title")}
      </h1>
      <Tabs defaultValue="info" className="w-full mx-auto">
        <TabsList
          className={cn(
            "grid w-full grid-cols-3 mb-6",
            isRTL ? "rtl" : "ltr"
          )}
        >
          <TabsTrigger value="info">{t("profile:page.tab1.title")}</TabsTrigger>
          <TabsTrigger value="security">
            {t("profile:page.tab2.title")}
          </TabsTrigger>
          <TabsTrigger value="appearance">
            {t("profile:page.tab3.title")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="info">
          <Card dir={isRTL ? "rtl" : "ltr"}>
            <CardHeader>
              <CardTitle
                className={isRTL ? "text-right" : "text-left"}
              >
                {t("profile:page.tab1.title")}
              </CardTitle>
              <CardDescription
                className={isRTL ? "text-right" : "text-left"}
              >
                {t("profile:page.tab1.description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col items-center justify-center mb-6">
                <Avatar className="h-32 w-32 mb-4">
                  <AvatarImage src={user?.user_metadata?.avatar_url} />
                  <AvatarFallback>
                    <div className="text-5xl font-bold">
                      {getInitials(user?.email || "")}
                    </div>
                  </AvatarFallback>
                </Avatar>
                <h2
                  className={cn(
                    "text-2xl font-semibold",
                    isRTL ? "text-right" : "text-left"
                  )}
                >
                  {user?.user_metadata?.name || user?.email}
                </h2>
                <p
                  className={cn(
                    "text-muted-foreground",
                    isRTL ? "text-right" : "text-left"
                  )}
                >
                  {user?.email}
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className={cn("space-y-1", isRTL && "text-right")}>
                  <p className="text-sm font-medium">
                    {t("profile:page.tab1.created")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {user.created_at
                      ? new Date(user.created_at).toLocaleString(
                          isRTL ? "ar-SA" : "en-US"
                        )
                      : "N/A"}
                  </p>
                </div>

                <div className={cn("space-y-1", isRTL && "text-right")}>
                  <p className="text-sm font-medium">
                    {t("profile:page.tab1.updated")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {user.updated_at
                      ? new Date(user.updated_at).toLocaleString(
                          isRTL ? "ar-SA" : "en-US"
                        )
                      : "N/A"}
                  </p>
                </div>
                <div className={cn("space-y-1", isRTL && "text-right")}>
                  <p className="text-sm font-medium">
                    {t("profile:page.tab1.last_signin")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {user.last_sign_in_at
                      ? new Date(user.last_sign_in_at).toLocaleString(
                          isRTL ? "ar-SA" : "en-US"
                        )
                      : "N/A"}
                  </p>
                </div>
                <div className={cn("space-y-1", isRTL && "text-right")}>
                  <p className="text-sm font-medium">
                    {t("profile:page.tab1.role")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {user.role || t("info.notAvailable")}
                  </p>
                </div>

                <div className={cn("space-y-1", isRTL && "text-right")}>
                  <p className="text-sm font-medium">
                    {t("profile:page.tab1.verified.title")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {user.email_confirmed_at
                      ? t("profile:page.tab1.verified.true")
                      : t("profile:page.tab1.verified.false")}
                  </p>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button
                variant="destructive"
                className={cn(
                  "w-full flex items-center justify-center",
                  isRTL ? "flex-row-reverse space-x-reverse space-x-2" : "space-x-2"
                )}
                onClick={() => {
                  signOut()
                  navigate("/")
                }}
              >
                <LogOut className="h-4 w-4" />
                <span>{t("common:buttons.signout")}</span>
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <Card dir={isRTL ? "rtl" : "ltr"}>
            <CardHeader>
              <CardTitle className={isRTL ? "text-right" : "text-left"}>
                {t("profile:page.tab2.security.title")}
              </CardTitle>
              <CardDescription className={isRTL ? "text-right" : "text-left"}>
                {t("profile:page.tab2.security.description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className={cn("text-lg font-medium", isRTL && "text-right")}>
                  {t("profile:page.tab2.password_reset.title")}
                </h3>
                <p className={cn("text-sm text-muted-foreground", isRTL && "text-right")}>
                  {t("profile:page.tab2.password_reset.description")}
                </p>

                {resetSent && (
                  <Alert className="bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-900/30 dark:text-green-400">
                    <Check className="h-4 w-4" />
                    <AlertTitle className={isRTL ? "text-right" : "text-left"}>
                      {t("profile:page.tab2.password_reset.email_sent")}
                    </AlertTitle>
                    <AlertDescription className={isRTL ? "text-right" : "text-left"}>
                      {t("profile:page.tab2.password_reset.check")}
                    </AlertDescription>
                  </Alert>
                )}

                <Button
                  onClick={handleResetPassword}
                  disabled={resetLoading}
                  className={cn(
                    "w-full",
                    isRTL && resetLoading && "flex-row-reverse"
                  )}
                >
                  {resetLoading && (
                    <Loader2 className={cn(
                      "h-4 w-4 animate-spin",
                      isRTL ? "ml-2" : "mr-2"
                    )} />
                  )}
                  {t("profile:page.tab2.password_reset.send")}
                </Button>
              </div>

              <div className="pt-4 border-t">
                <h3 className={cn("text-lg font-medium mb-2", isRTL && "text-right")}>
                  {t("profile:page.tab2.tips.title")}
                </h3>
                <ul className={cn(
                  "space-y-2 text-sm text-muted-foreground list-disc",
                  isRTL ? "pr-5 text-right list-inside" : "pl-5 list-outside"
                )}>
                  <li>{t("profile:page.tab2.tips.1")}</li>
                  <li>{t("profile:page.tab2.tips.2")}</li>
                  <li>{t("profile:page.tab2.tips.3")}</li>
                  <li>{t("profile:page.tab2.tips.4")}</li>
                  <li>{t("profile:page.tab2.tips.5")}</li>
                </ul>
              </div>
            </CardContent>
            <CardFooter>
              <Button
                variant="destructive"
                className={cn(
                  "w-full flex items-center justify-center",
                  isRTL ? "flex-row-reverse space-x-reverse space-x-2" : "space-x-2"
                )}
                onClick={() => {
                  signOut()
                  navigate("/")
                }}
              >
                <LogOut className="h-4 w-4" />
                <span>{t("common:buttons.signout")}</span>
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="appearance">
          <Card dir={isRTL ? "rtl" : "ltr"}>
            <CardHeader>
              <CardTitle className={isRTL ? "text-right" : "text-left"}>
                {t("profile:page.tab3.title")}
              </CardTitle>
              <CardDescription className={isRTL ? "text-right" : "text-left"}>
                {t("profile:page.tab3.description")}
              </CardDescription>
            </CardHeader>
            <CardContent></CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default ProfilePage

